import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { InventoryMockService } from '../services/inventory-mock.service';
import { InvKeyType, KEY_LABELS, OrgInventory, InventoryTxn, TransferParty } from '../models/inventory.model';
import { OrgMockService } from '../../../service/org-mock.service';
import { FsComboOption } from '../../common/fs-combobox/fs-combobox.component';

/**
 * The Unassigned bucket: somewhere to park keys not yet attached to an organization or buying
 * account. It holds a balance like an org — not an infinite source — so it transfers both ways.
 */
const UNASSIGNED: TransferParty = { id: 'unassigned', name: 'Unassigned' };

@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InventoryComponent implements OnInit {
  inv: OrgInventory | null = null;
  keyLabels = KEY_LABELS;
  keyTypes: InvKeyType[] = ['individual', 'group', 'team'];

  /** Tiles rendered as a plain balance. Team reports get their own tile — it doubles as the
   *  unlimited-upgrade promo, and reads as an entitlement rather than a count once upgraded. */
  countedKeyTypes: InvKeyType[] = ['individual', 'group'];

  filterType: 'all' | InvKeyType = 'all';
  search = '';
  page = 0;
  pageSize = 10;

  showTransfer = false;
  transferType: InvKeyType = 'individual';
  transferAmount: number | null = null;
  transferTried = false;

  /** 'unassigned' or an org id. Both ends default, both can be changed. */
  transferFromId = UNASSIGNED.id;
  transferToId = '';

  /** Unassigned + every org — both ends pick from the same list. */
  transferOptions: FsComboOption[] = [];

  displayName = '';

  constructor(private route: ActivatedRoute, private svc: InventoryMockService, private orgSvc: OrgMockService) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    // The org name isn't on the URL — the listing passes it via router state on the inventory links.
    this.displayName = window.history.state?.name || '';
    this.svc.getInventory(id, this.displayName || id).subscribe(inv => {
      this.inv = inv;
      this.buildOrgOptions();
    });
  }

  get pageTitle(): string { return this.displayName ? `${this.displayName} — Inventory` : 'Inventory'; }

  ledgerRows(): InventoryTxn[] {
    let rows = this.inv?.ledger ?? [];
    if (this.filterType !== 'all') { rows = rows.filter(t => t.keyType === this.filterType); }
    if (this.search) {
      const s = this.search.toLowerCase();
      rows = rows.filter(t =>
        t.ref.toLowerCase().includes(s) || t.item.toLowerCase().includes(s) ||
        t.org.toLowerCase().includes(s) || t.description.toLowerCase().includes(s));
    }
    return rows;
  }
  total(): number { return this.ledgerRows().length; }
  paged(): InventoryTxn[] { const s = this.page * this.pageSize; return this.ledgerRows().slice(s, s + this.pageSize); }
  pageCount(): number { return Math.max(1, Math.ceil(this.total() / this.pageSize)); }
  setPage(p: number): void { this.page = Math.max(0, Math.min(p, this.pageCount() - 1)); }
  setFilter(f: 'all' | InvKeyType): void { this.filterType = f; this.page = 0; }
  onSearch(): void { this.page = 0; }

  openTransfer(kt: InvKeyType): void {
    this.transferType = kt;
    this.transferAmount = null;
    this.transferTried = false;
    // Defaults, not constraints: keys usually come from Unassigned into the org you're looking
    // at, but either end can be changed.
    this.transferFromId = UNASSIGNED.id;
    this.transferToId = this.inv?.orgId ?? '';
    this.showTransfer = true;
  }
  closeTransfer(): void { this.showTransfer = false; }

  /** Unassigned + every org. The viewed org comes from the route, so it may not be seeded. */
  private buildOrgOptions(): void {
    const orgs = this.orgSvc.listSync().map(o => ({ id: o.id, name: o.name }));
    if (this.inv && !orgs.some(o => o.id === this.inv!.orgId)) {
      orgs.unshift({ id: this.inv.orgId, name: this.inv.orgName });
    }
    this.transferOptions = [{ ...UNASSIGNED }, ...orgs];
  }

  optionLabel(id: string): string {
    return this.transferOptions.find(o => o.id === id)?.name ?? '';
  }
  private party(id: string): TransferParty { return { id, name: this.optionLabel(id) }; }

  /** Keys the source can give — Unassigned has a balance like anything else. */
  get transferMax(): number {
    return this.svc.available(this.transferFromId, this.optionLabel(this.transferFromId), this.transferType);
  }

  get transferError(): string {
    const n = this.transferAmount;
    if (n === null || n === undefined || `${n}` === '') { return 'Enter how many keys to transfer.'; }
    if (n < 1 || Math.floor(n) !== n) { return 'Enter a whole number of at least 1.'; }
    if (!this.transferToId) { return 'Choose where the keys are going.'; }
    if (this.transferFromId === this.transferToId) { return 'Pick two different sources.'; }
    const max = this.transferMax;
    if (n > max) {
      return `${this.optionLabel(this.transferFromId)} only has ${max} ${this.keyLabels[this.transferType].toLowerCase()}.`;
    }
    return '';
  }

  doTransfer(): void {
    this.transferTried = true;
    if (!this.inv || this.transferError) { return; }
    this.svc.transfer({
      keyType: this.transferType,
      amount: this.transferAmount!,
      from: this.party(this.transferFromId),
      to: this.party(this.transferToId),
    }).subscribe(({ moved }) => {
      // Either end may be the org on screen (or neither) — re-read rather than assume.
      this.svc.getInventory(this.inv!.orgId, this.inv!.orgName).subscribe(updated => (this.inv = updated));
      this.showTransfer = false;
      Swal.fire({
        title: `${moved} key${moved === 1 ? '' : 's'} transferred`,
        text: `${this.optionLabel(this.transferFromId)} → ${this.optionLabel(this.transferToId)}`,
        icon: 'success', timer: 1600, showConfirmButton: false,
      });
    });
  }

  /**
   * Unlimited team reports is a billing change, not mock arithmetic like a key transfer — so this
   * stays a stated placeholder rather than flipping the entitlement locally. Wire it to the real
   * purchase flow and replace this; seeded orgs already cover the upgraded state for the UI.
   */
  upgradeTeamReports(): void {
    Swal.fire({
      title: 'Upgrade to unlimited',
      text: 'Unlimited Team Profile Reports will be available to purchase once billing is connected.',
      icon: 'info',
      confirmButtonText: 'Got it',
    });
  }
}
